import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { FileArrowUp, CheckCircle, WarningCircle, Sparkle, ArrowRight } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`);

const ConfidenceBadge = ({ value }) => {
  const map = {
    high: 'bg-green-900/40 text-green-300 border-green-700',
    medium: 'bg-amber-900/40 text-amber-300 border-amber-700',
    low: 'bg-red-900/40 text-red-300 border-red-700',
  };
  return (
    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${map[value] || map.low}`}>
      {value || 'low'}
    </span>
  );
};

// One editable review row. Staff can re-map the product, pick a flavour, set
// qty, and include/exclude the line.
const ReviewRow = ({ row, products, onChange }) => {
  const product = products.find((p) => p.id === row.productId) || null;
  const flavourKeys = product ? Object.keys(product.flavourCodes || {}) : [];
  const hasFlavours = flavourKeys.some((k) => k !== '');
  const sku = product ? (product.flavourCodes || {})[row.flavour ?? ''] : null;

  const pickProduct = (productId) => {
    const p = products.find((x) => x.id === productId);
    const keys = p ? Object.keys(p.flavourCodes || {}) : [];
    const firstFlavour = keys.includes('') ? '' : (keys[0] ?? '');
    onChange({ ...row, productId: productId || null, flavour: productId ? firstFlavour : null });
  };

  return (
    <tr
      className={`border-b border-border-color ${row.include ? '' : 'opacity-45'} ${row.productId ? '' : 'bg-amber-950/20'}`}
      data-testid={`po-review-row-${row.key}`}
    >
      <td className="px-2 py-2 align-top">
        <input
          type="checkbox"
          checked={row.include}
          onChange={(e) => onChange({ ...row, include: e.target.checked })}
          className="accent-gold mt-1"
          data-testid={`po-row-include-${row.key}`}
        />
      </td>
      <td className="px-2 py-2 align-top">
        <p className="text-sm text-text-primary leading-snug">{row.description}</p>
        <p className="text-[11px] text-text-secondary font-mono mt-0.5">
          {row.customerCode || 'no code'}
          {row.matchSource === 'remembered' && (
            <span className="ml-2 text-gold">★ remembered</span>
          )}
        </p>
      </td>
      <td className="px-2 py-2 align-top w-20">
        <input
          type="number"
          min={1}
          value={row.qty}
          onChange={(e) => onChange({ ...row, qty: e.target.value })}
          className="w-16 px-2 py-1 bg-black border border-border-color rounded text-text-primary text-sm text-center focus:border-gold focus:outline-none"
          data-testid={`po-row-qty-${row.key}`}
        />
      </td>
      <td className="px-2 py-2 align-top">
        <select
          value={row.productId || ''}
          onChange={(e) => pickProduct(e.target.value)}
          className="w-full min-w-[190px] px-2 py-1 bg-black border border-border-color rounded text-text-primary text-sm focus:border-gold focus:outline-none"
          data-testid={`po-row-product-${row.key}`}
        >
          <option value="">— not matched —</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}{p.size ? ` · ${p.size}` : ''}{p.range ? ` (${p.range})` : ''}
            </option>
          ))}
        </select>
        {hasFlavours && (
          <select
            value={row.flavour ?? ''}
            onChange={(e) => onChange({ ...row, flavour: e.target.value })}
            className="w-full min-w-[190px] mt-1 px-2 py-1 bg-black border border-border-color rounded text-text-primary text-sm focus:border-gold focus:outline-none"
            data-testid={`po-row-flavour-${row.key}`}
          >
            {flavourKeys.map((f) => (
              <option key={f} value={f}>{f || '(no flavour)'}</option>
            ))}
          </select>
        )}
      </td>
      <td className="px-2 py-2 align-top w-28">
        {sku ? (
          <span className="font-mono text-xs text-gold">{sku}</span>
        ) : (
          <span className="text-xs text-red-400 inline-flex items-center gap-1">
            <WarningCircle size={13} weight="bold" /> unmatched
          </span>
        )}
      </td>
      <td className="px-2 py-2 align-top w-16 text-center">
        <ConfidenceBadge value={row.confidence} />
      </td>
    </tr>
  );
};

export default function PoImportPage() {
  const [products, setProducts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [customerId, setCustomerId] = useState('');
  const [file, setFile] = useState(null);
  const [parsing, setParsing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null); // parse response
  const [rows, setRows] = useState([]);
  const [remember, setRemember] = useState(true);
  const [reference, setReference] = useState('');
  const [done, setDone] = useState(null); // confirm response

  useEffect(() => {
    axios.get(`${API}/products?active_only=true`).then((r) => setProducts(r.data || [])).catch(() => {});
    axios.get(`${API}/users`)
      .then((r) => setCustomers((r.data || []).filter((u) => u.role === 'customer' && u.active !== false)))
      .catch(() => {});
  }, []);

  const parse = useCallback(async () => {
    if (!file) { setError('Choose a PO PDF first.'); return; }
    setParsing(true); setError(null); setResult(null); setRows([]); setDone(null);
    try {
      const form = new FormData();
      form.append('file', file);
      const url = customerId ? `${API}/orders/import-po?customerId=${customerId}` : `${API}/orders/import-po`;
      const { data } = await axios.post(url, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      setReference(data.poNumber || '');
      if (!customerId && data.detectedCustomerId) setCustomerId(data.detectedCustomerId);
      setRows((data.lines || []).map((l, i) => ({
        key: i,
        description: l.description,
        customerCode: l.customerCode,
        qty: l.quantity,
        productId: l.productId,
        flavour: l.flavour,
        confidence: l.confidence,
        matchSource: l.matchSource,
        include: l.matched, // matched lines are on by default; unmatched need a manual pick
      })));
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to read PO');
    } finally {
      setParsing(false);
    }
  }, [file, customerId]);

  const readyLines = useMemo(
    () => rows.filter((r) => r.include && r.productId && Number(r.qty) > 0),
    [rows],
  );

  const confirm = useCallback(async () => {
    if (!customerId) { setError('Select which customer this PO belongs to.'); return; }
    if (readyLines.length === 0) { setError('No included, matched lines to import.'); return; }
    setConfirming(true); setError(null);
    try {
      const payload = {
        customerId,
        poNumber: result?.poNumber || null,
        customerReference: reference || result?.poNumber || null,
        rememberMappings: remember,
        lines: readyLines.map((r) => {
          const p = products.find((x) => x.id === r.productId);
          const sku = p ? (p.flavourCodes || {})[r.flavour ?? ''] : null;
          return {
            productId: r.productId,
            flavour: r.flavour,
            sku: sku || null,
            qty: Number(r.qty),
            customerCode: r.customerCode || null,
          };
        }),
      };
      const { data } = await axios.post(`${API}/orders/import-po/confirm`, payload);
      setDone(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || 'Failed to create order');
    } finally {
      setConfirming(false);
    }
  }, [customerId, readyLines, remember, reference, result, products]);

  const updateRow = (key, next) => setRows((rs) => rs.map((r) => (r.key === key ? next : r)));

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="po-import-page">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-text-primary flex items-center gap-2">
          <FileArrowUp size={28} weight="duotone" className="text-gold" /> Import Customer PO
        </h1>
        <p className="text-sm text-text-secondary mt-1 max-w-2xl">
          Upload a customer&apos;s purchase-order PDF (e.g. Sprint Fit). The app reads it, matches each
          line to your catalogue, and lets you review before it becomes a submitted order. Learned
          product-code mappings are remembered so repeat POs from the same customer resolve instantly.
        </p>
      </div>

      {/* Step 1 — pick customer + file */}
      <div className="bg-surface border border-border-color rounded-xl p-4 mb-5">
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="block text-xs text-text-secondary mb-1">Customer</label>
            <select
              value={customerId}
              onChange={(e) => setCustomerId(e.target.value)}
              data-testid="po-customer-select"
              className="w-full px-3 py-2 bg-black border border-border-color rounded-lg text-text-primary focus:border-gold focus:outline-none"
            >
              <option value="">— Select customer —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.email})</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-text-secondary mb-1">Purchase-order PDF</label>
            <input
              type="file"
              accept="application/pdf,.pdf"
              onChange={(e) => { setFile(e.target.files?.[0] || null); setResult(null); setRows([]); setDone(null); }}
              data-testid="po-file-input"
              className="w-full text-sm text-text-secondary file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-gold file:text-black file:font-bold file:cursor-pointer"
            />
          </div>
        </div>
        <div className="mt-3">
          <button
            onClick={parse}
            disabled={parsing || !file}
            data-testid="po-parse-button"
            className="px-4 py-2 bg-gold text-black text-sm font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50 inline-flex items-center gap-2"
          >
            {parsing ? (<><Sparkle size={16} weight="fill" className="animate-pulse" /> Reading PO…</>) : (<><Sparkle size={16} weight="fill" /> Read &amp; match PO</>)}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-5 p-3 bg-red-950/40 border border-red-800 rounded-lg text-red-300 text-sm" data-testid="po-error">
          {error}
        </div>
      )}

      {/* Success */}
      {done && (
        <div className="mb-5 p-4 bg-green-950/40 border border-green-800 rounded-lg" data-testid="po-success">
          <p className="text-green-300 font-semibold flex items-center gap-2">
            <CheckCircle size={20} weight="fill" /> Order {done.orderNumber} created ({done.unitCount} units, {done.lineCount} lines).
          </p>
          <p className="text-sm text-text-secondary mt-1">
            Customer reference <span className="font-mono text-text-primary">{done.customerReference || '—'}</span> ·
            {' '}{done.mappingsSaved} code mapping(s) remembered.
          </p>
          <Link to="/submitted-orders" className="mt-2 inline-flex items-center gap-1 text-gold text-sm hover:underline" data-testid="po-goto-submitted">
            View in Submitted Orders <ArrowRight size={14} weight="bold" />
          </Link>
        </div>
      )}

      {/* Step 2 — review */}
      {result && !done && (
        <div className="bg-surface border border-border-color rounded-xl p-4" data-testid="po-review-panel">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 mb-4 text-sm">
            <span className="text-text-secondary">PO <span className="font-mono text-text-primary">{result.poNumber || '—'}</span></span>
            <span className="text-text-secondary">From <span className="text-text-primary">{result.senderEmail || '—'}</span></span>
            <span className="text-text-secondary">
              Matched <span className="text-gold font-bold">{result.matchedCount}</span> / {result.lineCount}
            </span>
            <span className="text-text-secondary">Importing <span className="text-gold font-bold" data-testid="po-ready-count">{readyLines.length}</span> line(s)</span>
          </div>

          <p className="text-[11px] text-text-secondary mb-2">
            JAFFA and Creapure lines are automatically excluded. Amber rows aren&apos;t matched yet — pick a
            product to include them. Tick a line to include it in the order.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-text-secondary border-b border-border-color">
                  <th className="px-2 py-2 w-8">Inc.</th>
                  <th className="px-2 py-2">PO line</th>
                  <th className="px-2 py-2">Qty</th>
                  <th className="px-2 py-2">Raisey&apos;s product</th>
                  <th className="px-2 py-2">SKU</th>
                  <th className="px-2 py-2 text-center">AI</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <ReviewRow key={r.key} row={r} products={products} onChange={(next) => updateRow(r.key, next)} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap items-center gap-4">
            <div>
              <label className="block text-[11px] text-text-secondary mb-1">Customer reference (PO no.)</label>
              <input
                value={reference}
                onChange={(e) => setReference(e.target.value)}
                data-testid="po-reference-input"
                className="px-3 py-1.5 bg-black border border-border-color rounded-lg text-text-primary text-sm focus:border-gold focus:outline-none"
              />
            </div>
            <label className="flex items-center gap-2 text-sm text-text-secondary mt-4">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-gold" data-testid="po-remember-toggle" />
              Remember these code → product mappings for this customer
            </label>
            <button
              onClick={confirm}
              disabled={confirming || readyLines.length === 0 || !customerId}
              data-testid="po-confirm-button"
              className="ml-auto mt-4 px-5 py-2 bg-gold text-black text-sm font-bold rounded-lg hover:bg-gold-hover disabled:opacity-50 inline-flex items-center gap-2"
            >
              {confirming ? 'Creating order…' : `Create submitted order (${readyLines.length})`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
